"use client"

import { useState } from "react"
import { ChevronDown, ChevronRight, Package } from "lucide-react"
import type { RichCard } from "@/lib/agent-demo-data"
import { DeliverableCard } from "./deliverable-card"
import { AudioPlayerCard } from "./audio-player-card"
import { ImageGalleryCard } from "./image-gallery-card"
import { PptPreviewCard } from "./ppt-preview-card"
import { AlertCard } from "./alert-card"
import { DataTableCard } from "./data-table-card"
import { BreakdownCard } from "./breakdown-card"

/**
 * Renders a RichCard union type into the appropriate UI component.
 */
export function RichCardRenderer({ card }: { card: RichCard }) {
  switch (card.type) {
    case "deliverables":
      return (
        <DeliverableCard
          title={card.title}
          files={card.files as any}
        />
      )
    case "audio":
      return (
        <AudioPlayerCard
          title={card.title}
          subtitle={card.subtitle}
          duration={card.duration}
          format={card.format}
        />
      )
    case "images":
      return (
        <ImageGalleryCard
          title={card.title}
          images={card.images}
        />
      )
    case "ppt":
      return (
        <PptPreviewCard
          filename={card.filename}
          pageCount={card.pageCount}
          slides={card.slides}
        />
      )
    case "alert":
      return (
        <AlertCard
          title={card.title}
          items={card.items}
        />
      )
    case "table":
      return (
        <DataTableCard
          title={card.title}
          columns={card.columns}
          rows={card.rows as any}
        />
      )
    case "breakdown":
      return (
        <BreakdownCard
          title={card.title}
          total={card.total}
          unit={card.unit}
          items={card.items}
        />
      )
    default:
      return null
  }
}

function cardLabel(card: RichCard): string {
  switch (card.type) {
    case "deliverables": return `${card.files.length} 个文件`
    case "audio": return "音频"
    case "images": return `${card.images.length} 张图`
    case "ppt": return "PPT"
    case "alert": return `${card.items.length} 条`
    case "table": return `${card.rows.length} 行`
    case "breakdown": return `${card.items.length} 类`
    default: return ""
  }
}

/**
 * Renders an array of RichCards with collapsible wrapper.
 * Collapsed by default in chat, showing a compact summary bar.
 */
export function RichCardsBlock({
  cards,
  defaultExpanded = false,
}: {
  cards: RichCard[]
  defaultExpanded?: boolean
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)

  // Build a compact summary of what's inside
  const summary = cards.map(cardLabel).filter(Boolean).join(" · ")

  return (
    <div className="mt-3">
      {/* Collapse toggle bar */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-primary/5 border border-primary/10 hover:bg-primary/10 transition-colors text-left group"
      >
        <Package className="h-3.5 w-3.5 text-primary shrink-0" />
        <span className="text-xs font-medium text-primary flex-1 truncate">
          {expanded ? "收起结果详情" : "查看结果详情"}
        </span>
        <span className="text-micro text-muted-foreground">{summary}</span>
        {expanded
          ? <ChevronDown className="h-3 w-3 text-primary/60 shrink-0" />
          : <ChevronRight className="h-3 w-3 text-primary/60 shrink-0" />}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="mt-2 space-y-2.5 animate-in fade-in slide-in-from-top-1 duration-200">
          {cards.map((card, i) => (
            <div
              key={i}
              className="animate-in fade-in slide-in-from-bottom-2 duration-300"
              style={{ animationDelay: `${i * 100}ms`, animationFillMode: "both" }}
            >
              <RichCardRenderer card={card} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Compact version for sidebar — shows only first card + count.
 */
export function RichCardsCompact({ cards }: { cards: RichCard[] }) {
  if (cards.length === 0) return null

  // In sidebar, only show deliverables compact + count of other items
  const deliverables = cards.find(c => c.type === "deliverables")
  const otherCount = cards.filter(c => c.type !== "deliverables").length

  return (
    <div className="animate-in fade-in duration-300 rounded-lg border border-primary/15 bg-primary/5 px-3 py-2">
      <div className="flex items-center gap-2">
        <Package className="h-3.5 w-3.5 text-primary shrink-0" />
        <span className="text-xs font-medium text-primary">
          结果已生成
        </span>
      </div>
      {deliverables && deliverables.type === "deliverables" && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {deliverables.files.slice(0, 3).map((f, i) => (
            <span
              key={i}
              className="inline-flex items-center px-1.5 py-0.5 rounded bg-background/60 text-micro text-muted-foreground"
            >
              {f.name}
            </span>
          ))}
          {deliverables.files.length > 3 && (
            <span className="text-micro text-muted-foreground">
              +{deliverables.files.length - 3} 个文件
            </span>
          )}
        </div>
      )}
      {otherCount > 0 && (
        <p className="text-micro text-muted-foreground mt-1">
          还有 {otherCount} 项附件（图表/音频/文档）
        </p>
      )}
    </div>
  )
}
